import clsx from "clsx";
import type { ReactNode } from "react";
import { CategoryIcon } from "@/components/ui/CategoryIcon";
import { StatusText } from "@/components/ui/Feedback";
import { formatMoney, formatPercent } from "@/lib/format";
import type { BudgetStatus, CategoryShare } from "@/lib/types";

/**
 * Stat tile: label · value · optional note.
 *
 * `tone` draws a bar down the leading edge. It is deliberately not applied
 * to the figure itself: a number painted red reads as an error rather than
 * as a negative balance, and a reader with a colour vision deficiency gets
 * nothing from it. The bar is a marker beside the value, and the note
 * underneath says in words what the colour hints at - so the meaning never
 * rests on colour alone.
 *
 * The container query is there because `overflow-hidden` on a tile turns
 * "too wide" into "silently truncated", and a truncated amount is a wrong
 * amount: two tiles abreast on a 320px screen leave about 106px of text, and
 * "Ksh 1,453,000.00" wants 131px at the headline size, so it was rendering
 * as "Ksh 1,453,000.0". Money never wraps mid-number (Money sets
 * whitespace-nowrap, which is what stops "8,527,098.4 / 1"), so the figure
 * has to get smaller rather than break. It steps down only in tiles this
 * narrow; at 390px and up nothing changes.
 */
export function StatTile({ label, value, note, emphasis, tone, className }: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  emphasis?: boolean;
  tone?: "good" | "bad" | "neutral";
  className?: string;
}) {
  return (
    <div role="group" aria-label={label}
         className={clsx(
           "@container relative min-w-0 overflow-hidden rounded-xl border border-border bg-surface p-4",
           tone && "pl-[calc(1rem-3px)] border-l-[3px]",
           tone === "good" && "border-l-[var(--good-ink)]",
           tone === "bad" && "border-l-[var(--bad-ink)]",
           tone === "neutral" && "border-l-[var(--accent)]",
           className,
         )}>
      <p className="text-xs font-medium text-ink-3">{label}</p>
      <p className={clsx("mt-1 break-words font-semibold tracking-tight text-ink",
                         emphasis
                           ? "text-2xl @max-[7.5rem]:text-base sm:text-3xl"
                           : "text-lg @max-[7.5rem]:text-sm sm:text-xl")}>
        {value}
      </p>
      {note && <div className="mt-1 text-xs text-ink-3">{note}</div>}
    </div>
  );
}

/**
 * Ranked horizontal bars for one series (spending by category, income by source).
 * One series → one colour; the values are printed, so the list is its own table view.
 */
export function ShareBars({ rows, currency, empty, limit = 8 }: {
  rows: CategoryShare[];
  currency: string;
  empty: string;
  limit?: number;
}) {
  if (rows.length === 0) return <p className="py-6 text-center text-sm text-ink-3">{empty}</p>;
  const shown = rows.slice(0, limit);
  const hidden = rows.length - shown.length;
  // Bar length is geometry only; every figure shown is the server's.
  const max = Math.max(...rows.map((r) => Number(r.amount)), 1);
  return (
    <>
    <ul className="flex flex-col gap-3">
      {shown.map((r) => {
        const pct = Math.max(0, (Number(r.amount) / max) * 100);
        return (
          <li key={r.category_id}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="inline-flex min-w-0 items-center gap-2 text-ink">
                <CategoryIcon slug={r.icon} className="size-4 shrink-0 text-ink-3" />
                <span className="truncate">{r.name}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="num font-medium text-ink">{formatMoney(r.amount, currency)}</span>
                <span className="num w-10 text-right text-xs text-ink-3">{formatPercent(r.share)}</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-surface-2" aria-hidden>
              <div className="h-2 rounded-full bg-series-1" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
    {hidden > 0 && (
      <p className="mt-3 text-xs text-ink-3">
        and {hidden} more {hidden === 1 ? "category" : "categories"} — see Reports for the full list
      </p>
    )}
    </>
  );
}

/** Budget meter: same-ramp track; the fill carries severity; status always has icon + text. */
export function BudgetMeter({ percent, status, statusText, compact }: {
  percent: string | null;
  status: BudgetStatus;
  statusText: string;
  compact?: boolean;
}) {
  const pct = Math.min(100, Math.max(0, Number(percent ?? 0)));
  const fill =
    status === "over" ? "bg-status-critical" : status === "warning" ? "bg-status-warning" : "bg-series-1";
  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(Number(percent ?? 0))}
        aria-valuetext={statusText}
        className={clsx("w-full rounded-full bg-meter-track", compact ? "h-1.5" : "h-2.5")}
      >
        <div className={clsx("rounded-full", fill, compact ? "h-1.5" : "h-2.5")} style={{ width: `${pct}%` }} />
      </div>
      {!compact && <StatusText status={status}>{statusText}</StatusText>}
    </div>
  );
}
